import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, AlertTriangle, Clock, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import { safeText } from '@/lib/safeText';

const TYPE_ICONS = {
  overdue_obligation: Clock,
  ld_cap_alert: AlertTriangle,
  claim_update: Shield,
  info: Bell,
};

const TYPE_COLORS = {
  overdue_obligation: 'text-red-600 bg-red-50',
  ld_cap_alert: 'text-amber-600 bg-amber-50',
  claim_update: 'text-blue-600 bg-blue-50',
  info: 'text-[#1E4E8C] bg-[#1E4E8C]/10',
};

export function NotificationsPanel() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.items || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await api.post('/notifications/mark-all-read');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const s = Math.floor((Date.now() - d) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button variant="ghost" size="icon" className="h-8 w-8 relative text-gray-500" onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        data-testid="notifications-btn">
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[#1E4E8C] text-white text-[9px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
            {unreadCount}
          </span>
        )}
      </Button>
      {open && (
        <div className="absolute right-0 top-10 w-80 rounded-xl border border-gray-200 bg-white shadow-xl z-50" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#1E4E8C]" onClick={markAllRead} data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400" onClick={() => setOpen(false)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <ScrollArea className="max-h-80">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications
              </div>
            ) : (
              <div className="p-1">
                {notifications.map(n => {
                  const Icon = TYPE_ICONS[n.type] || Bell;
                  const colors = TYPE_COLORS[n.type] || TYPE_COLORS.info;
                  return (
                    <div key={n.notification_id}
                      className={`flex gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${n.read ? 'opacity-50 hover:opacity-70' : 'hover:bg-gray-50'}`}
                      onClick={() => !n.read && markRead(n.notification_id)}
                      data-testid={`notification-item-${n.notification_id}`}>
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${colors}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900">{safeText(n.title)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{safeText(n.message)}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-[#1E4E8C] mt-2 shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
